/**
 * Audit Service
 * 
 * Centralized service for managing audit trail logging and retrieval.
 * Uses localStorage for MVP implementation with easy migration path to backend API.
 */

import type { AuditEntry, AuditActionType, AuditTrail, AuditChange, AuditSummary } from '@/types/audit';
import type { Timesheet, TimesheetStatus } from '@/types/timesheet';

export class AuditService {
  private static instance: AuditService;
  private readonly STORAGE_KEY = 'timesheet_audit_trails';
  
  private constructor() {}
  
  /**
   * Get singleton instance
   */
  static getInstance(): AuditService {
    if (!AuditService.instance) {
      AuditService.instance = new AuditService();
    }
    return AuditService.instance;
  }
  
  /**
   * Log an audit entry
   */
  logAction(
    timesheetId: string,
    actionType: AuditActionType,
    actorId: string,
    actorName: string,
    options?: {
      previousStatus?: TimesheetStatus;
      newStatus?: TimesheetStatus;
      changes?: AuditChange[];
      comment?: string;
      actorRole?: string;
      timestamp?: Date;
    }
  ): void {
    try {
      const auditEntry: AuditEntry = {
        id: `audit-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        timesheetId,
        timestamp: options?.timestamp || new Date(),
        actionType,
        actorId,
        actorName,
        actorRole: options?.actorRole,
        previousStatus: options?.previousStatus,
        newStatus: options?.newStatus,
        changes: options?.changes,
        comment: options?.comment,
      };
      
      this.saveAuditEntry(auditEntry);
    } catch (error) {
      console.error('Failed to log audit entry:', error);
      // Don't throw - audit logging should not break main functionality
    }
  }
  
  /**
   * Get audit trail for a timesheet
   */
  getAuditTrail(timesheetId: string): AuditEntry[] {
    try {
      const trails = this.getAuditTrails();
      const trail = trails.get(timesheetId);
      return trail?.entries || [];
    } catch (error) {
      console.error('Failed to get audit trail:', error);
      return [];
    }
  }
  
  /**
   * Get audit summary (for display)
   */
  getAuditSummary(timesheetId: string): AuditSummary {
    try {
      const entries = this.getAuditTrail(timesheetId);
      
      const created = entries.find(e => e.actionType === 'created');
      const submitted = entries.find(e => e.actionType === 'submitted');
      const approved = entries.find(e => e.actionType === 'approved');
      const rejected = entries.find(e => e.actionType === 'rejected');
      const lastModified = entries
        .filter(e => ['saved_draft', 'modified'].includes(e.actionType))
        .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())[0];
      
      return {
        createdAt: created?.timestamp,
        createdBy: created?.actorName,
        lastModifiedAt: lastModified?.timestamp,
        lastModifiedBy: lastModified?.actorName,
        submittedAt: submitted?.timestamp,
        approvedAt: approved?.timestamp,
        approvedBy: approved?.actorName,
        rejectedAt: rejected?.timestamp,
        rejectedBy: rejected?.actorName,
        totalChanges: entries.length,
      };
    } catch (error) {
      console.error('Failed to get audit summary:', error);
      return { totalChanges: 0 };
    }
  }
  
  /**
   * Detect changes between timesheet versions
   */
  detectChanges(oldTimesheet: Timesheet, newTimesheet: Timesheet): AuditChange[] {
    const changes: AuditChange[] = [];
    
    try {
      // Compare entries
      const oldEntryMap = new Map(oldTimesheet.entries.map(e => [e.id, e]));
      const newEntryMap = new Map(newTimesheet.entries.map(e => [e.id, e]));
      
      // Detect added entries
      newTimesheet.entries.forEach(entry => {
        if (!oldEntryMap.has(entry.id)) {
          changes.push({
            field: 'entry_added',
            oldValue: null,
            newValue: {
              projectCode: entry.projectCode,
              projectName: entry.projectName,
              taskCode: entry.taskCode,
              description: entry.description,
            },
            entryId: entry.id,
          });
        }
      });
      
      // Detect removed entries
      oldTimesheet.entries.forEach(entry => {
        if (!newEntryMap.has(entry.id)) {
          changes.push({
            field: 'entry_removed',
            oldValue: {
              projectCode: entry.projectCode,
              projectName: entry.projectName,
              taskCode: entry.taskCode,
              description: entry.description,
            },
            newValue: null,
            entryId: entry.id,
          });
        }
      });
      
      // Detect modified entries
      oldTimesheet.entries.forEach(oldEntry => {
        const newEntry = newEntryMap.get(oldEntry.id);
        if (newEntry) {
          // Compare hours for each day
          const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;
          days.forEach(day => {
            if (oldEntry.hours[day] !== newEntry.hours[day]) {
              changes.push({
                field: `hours_${day}`,
                oldValue: oldEntry.hours[day],
                newValue: newEntry.hours[day],
                entryId: oldEntry.id,
              });
            }
          });
          
          // Compare other fields
          if (oldEntry.description !== newEntry.description) {
            changes.push({
              field: 'description',
              oldValue: oldEntry.description,
              newValue: newEntry.description,
              entryId: oldEntry.id,
            });
          }
          
          if (oldEntry.projectCode !== newEntry.projectCode) {
            changes.push({
              field: 'project',
              oldValue: `${oldEntry.projectCode} - ${oldEntry.projectName}`,
              newValue: `${newEntry.projectCode} - ${newEntry.projectName}`,
              entryId: oldEntry.id,
            });
          }
          
          if (oldEntry.taskCode !== newEntry.taskCode) {
            changes.push({
              field: 'task',
              oldValue: oldEntry.taskCode,
              newValue: newEntry.taskCode,
              entryId: oldEntry.id,
            });
          }
        }
      });
    } catch (error) {
      console.error('Failed to detect changes:', error);
    }
    
    return changes;
  }
  
  /**
   * Clear all audit trails (for testing/development)
   */
  clearAllAuditTrails(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch (error) {
      console.error('Failed to clear audit trails:', error);
    }
  }
  
  /**
   * Save a single audit entry
   */
  private saveAuditEntry(entry: AuditEntry): void {
    const trails = this.getAuditTrails();
    let trail = trails.get(entry.timesheetId);
    
    if (!trail) {
      trail = {
        timesheetId: entry.timesheetId,
        entries: [],
        createdAt: new Date(),
        lastModifiedAt: new Date(),
      };
    }
    
    trail.entries.push(entry);
    trail.lastModifiedAt = new Date();
    trails.set(entry.timesheetId, trail);
    
    this.saveAuditTrails(trails);
  }
  
  /**
   * Get all audit trails from storage
   */
  private getAuditTrails(): Map<string, AuditTrail> {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (!stored) return new Map();
      
      const parsed = JSON.parse(stored);
      return new Map(Object.entries(parsed).map(([k, v]: [string, any]) => [
        k,
        {
          ...v,
          entries: v.entries.map((e: any) => ({
            ...e,
            timestamp: new Date(e.timestamp)
          })),
          createdAt: new Date(v.createdAt),
          lastModifiedAt: new Date(v.lastModifiedAt)
        }
      ]));
    } catch (error) {
      console.error('Failed to load audit trails:', error);
      return new Map();
    }
  }
  
  /**
   * Save all audit trails to storage
   */
  private saveAuditTrails(trails: Map<string, AuditTrail>): void {
    try {
      const obj = Object.fromEntries(trails);
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(obj));
    } catch (error) {
      console.error('Failed to save audit trails:', error);
      
      // Check if localStorage is full
      if (error instanceof DOMException && error.name === 'QuotaExceededError') {
        console.warn('localStorage quota exceeded. Consider implementing cleanup or using IndexedDB.');
        // Could implement automatic cleanup of old audit trails here
      }
    }
  }
}

// Export singleton instance
export const auditService = AuditService.getInstance();
